import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Gamepad2,
  Settings,
  ImageIcon,
  FileText,
  ScrollText,
  DatabaseBackup,
  LogOut,
  Menu,
  X,
  Layers,
  ImagePlus,
  Users,
  FolderOpen,
  Code2,
  Search,
  Clock,
  Sparkles,
  Star,
  ShieldAlert,
  Scale,
  Smartphone,
  Headphones,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/admin/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/ia", label: "Assistente IA", icon: Sparkles },
  { to: "/admin/jogos", label: "Jogos", icon: Gamepad2 },
  { to: "/admin/plataformas", label: "Lançamentos do momento", icon: Star },
  { to: "/admin/atendimento", label: "Atendimento", icon: Headphones },
  { to: "/admin/aplicativo", label: "Aplicativo", icon: Smartphone },
  { to: "/admin/categorias", label: "Categorias", icon: Layers },
  { to: "/admin/banners", label: "Banners", icon: ImagePlus },
  { to: "/admin/imagens", label: "Imagens", icon: ImageIcon },
  { to: "/admin/arquivos", label: "Arquivos", icon: FolderOpen },
  { to: "/admin/prompts", label: "Prompts IA", icon: FileText },
  { to: "/admin/editor", label: "Editor de Código", icon: Code2 },
  { to: "/admin/usuarios", label: "Admins", icon: Users },
  { to: "/admin/seo", label: "SEO", icon: Search },
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings },
  { to: "/admin/documentos-legais", label: "Termos & Privacidade", icon: Scale },
  { to: "/admin/logs", label: "Logs", icon: ScrollText },
  { to: "/admin/backup", label: "Backup", icon: DatabaseBackup },
];

export const AdminLayout = () => {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    toast.success("Sessão encerrada");
    nav("/admin/login", { replace: true });
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-5 h-[72px] border-b border-white/[0.06]">
        <img src="/manga-symbol.png" alt="" className="w-9 h-9 object-contain gold-glow" />
        <div>
          <div className="font-display font-extrabold text-white text-sm leading-none">
            GRUPO <span className="text-gold-gradient">MANGA</span>
          </div>
          <div className="text-[9px] text-white/40 uppercase tracking-widest mt-1">CMS Admin</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-[#F5C542]/10 text-[#F5C542] border border-[#F5C542]/25"
                  : "text-white/60 hover:text-white hover:bg-white/[0.04] border border-transparent"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-white/[0.06]">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs text-white/70 truncate">{user?.email}</div>
          <div className="text-[10px] text-[#F5C542] uppercase tracking-wider">Super Admin</div>
        </div>
        <button
          onClick={doLogout}
          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-white/60 hover:text-[#FF3B30] hover:bg-[#FF3B30]/5 transition-colors"
          data-testid="admin-logout"
        >
          <LogOut className="w-4 h-4" /> Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#120D0D] text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 border-r border-white/[0.06] bg-[#0c0c0c] sticky top-0 h-screen">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#0c0c0c] border-r border-white/10 h-full">
            <SidebarInner />
          </div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <header className="lg:hidden flex items-center justify-between h-16 px-4 border-b border-white/[0.06] bg-[#0c0c0c] sticky top-0 z-40">
          <button onClick={() => setOpen(true)} className="text-white" data-testid="admin-menu-toggle">
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-display font-bold">Painel Admin</span>
          <span className="w-6" />
        </header>
        <main className="p-4 md:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
